import { DateTime } from 'luxon';
import * as React from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import "react-widgets/styles.css";
import { ListGroup, ListGroupItem, Button, Col, Collapse, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Concept, ConceptRef, DimensionalConceptRef } from '../../redux/ConceptSlice';
import { ConceptNode } from '../../redux/AccountsSlice';
import { Node, selectOENode, selectSDNode, selectUnitHierarchy } from '../../redux/EntitiesSlice';
import {
    addDeprecation, ConceptInput, EditDeprecation, getChangeConceptLabel,
    getConceptChanges, getDeprecationFor, getDeprecations, getEditableDeprecation,
    getRelationshipChanges, RelationshipInput
} from '../../redux/TaxonomyChangesSlice';
import {
    DtsConcept, getDeprecationDate, getDeprecationLabel, getSelectedTaxonomyConcept,
    loadTaxonomyConcept, setSelectedTaxonomyConcept
} from '../../redux/TaxonomyConceptSlice';
import { clearSelectedReference } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion, TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import utilityHelper from '../../utils/utilityHelper';
import CreateConceptModal from '../layout/Modal/CreateConceptModal';



export interface StyledSelectableListItem {
    label: string;
    value: string;
    disabled: boolean;
    checked: boolean;
    className: string;
    children: Array<StyledSelectableListItem>;
}

const isEntityCode = function (cid: string): boolean {
    if (cid && cid.length === 6) {
        // This is a candidate entity id
        return !isNaN(parseInt(cid));
    }
    return false;
}

export const addNode = function (parent: StyledSelectableListItem | null, n: Node, unrestricted: boolean): StyledSelectableListItem {
    var nodeLabel = n.name;
    var nvalue = n.id;
    var isEntity = false;
    var nconcept = n.concept;
    if (nconcept) {
        nodeLabel = nconcept.name;
        nvalue = nconcept.id;
        isEntity = isEntityCode(nconcept.code);
    }
    var listItem: StyledSelectableListItem = {
        label: nodeLabel,
        value: nvalue,
        disabled: false,
        checked: false,
        className: '',//getNodeIcon(concept),
        children: []
    }
    if (parent) {
        if (unrestricted) {
            parent.children.push(listItem);
        } else {
            if (!isEntity) {
                parent.children.push(listItem);
            }
            return listItem;
        }
    }

    if (n.outgoing) {
        n.outgoing.map(e => addNode(listItem, e.to, unrestricted));
    }
    return listItem;
}

const getUnitsList = function (unitHierarchy: Node | null, conceptChanges : ConceptInput[], relationshipChanges : RelationshipInput[], versionedConcept : DtsConcept | null ): Array<StyledSelectableListItem> {
    var alist: Array<StyledSelectableListItem> = [];
    var selId: string = (versionedConcept && versionedConcept.concept) ? versionedConcept.concept.id : "none";
    if (unitHierarchy && unitHierarchy.outgoing) {
        unitHierarchy.outgoing.map(e => {
            if (e.to.concept.name === "Other Entity" ||
                e.to.concept.name === "Special District") {
                var rootItem: StyledSelectableListItem = addNode(null, e.to, false);
                rootItem.children.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
                alist.push(rootItem);
            }
        })

    }

    var itemMap: any = {};
    alist.map(item => itemMap[item.value] = item);
    if (conceptChanges.length > 0) {
        var itemParentMap: any = {};
        relationshipChanges.map(rchg => (itemMap.hasOwnProperty(rchg.fromId)) ? itemParentMap[rchg.toId] = itemMap[rchg.fromId] : "");
        conceptChanges.map(function (cchg: ConceptInput, index) {
            // Note: we have switched to id rather than code
            //                var chgCode = getChangeConceptLabel(cchg, "code");

            if (itemParentMap.hasOwnProperty(cchg.id)) {
                var selItem: StyledSelectableListItem = itemParentMap[cchg.id];
                var listItem: StyledSelectableListItem = {
                    label: getChangeConceptLabel(cchg, "label"),
                    value: cchg.id,
                    disabled: false,
                    checked: (cchg.id === selId),
                    children: [],
                    className: "bi bi-file-plus-fill"
                }
                selItem.children.push(listItem);
                selItem.children.sort((a, b) => a.label.localeCompare(b.label));
            }
        });
    }
    return alist;
}

export const getUnitsListNoChanges = function (unitHierarchy: Node | null): Array<StyledSelectableListItem> {
    var alist: Array<StyledSelectableListItem> = [];
    if (unitHierarchy && unitHierarchy.outgoing) {
        unitHierarchy.outgoing.map(e => {
            var rootItem: StyledSelectableListItem = addNode(null, e.to,true);
            rootItem.children.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
            alist.push(rootItem);
        })

    }

    return alist;
}

const DistrictTypeManager = () => {

    const dispatch = useAppDispatch();

    // selectors
    var versionedConcept: DtsConcept | null = useAppSelector(getSelectedTaxonomyConcept);
    var deprecations: EditDeprecation[] = useAppSelector(getDeprecations);
    var concept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    const conceptChanges = useAppSelector(getConceptChanges);
    const unitHierarchySelector: Node | null = useAppSelector(selectUnitHierarchy);
    const relationshipChanges = useAppSelector(getRelationshipChanges);
    const specialDistrictNodeSelector: Node | null = useAppSelector(selectSDNode);
    const otherEntityNodeSelector: Node | null = useAppSelector(selectOENode);

    //constants

    const [mode, setMode] = React.useState<"inform" | "create" | "deprecate">("inform");
    const [deprecation, setDeprecation] = React.useState<EditDeprecation>(getCurrentDeprecation(versionedConcept));
    const [childList, setChildList] = React.useState<string[]>(getChildList(unitHierarchySelector));
    //functions



    function getCurrentDeprecation(c: DtsConcept | null): EditDeprecation {
        if (!versionedConcept) {
            return utilityHelper.emptyDeprecation;
        }
        if (!c) {
            return utilityHelper.emptyDeprecation;
        }

        var concept = c.concept;
        if (!concept) {
            return utilityHelper.emptyDeprecation;
        }
        var pendingDep: EditDeprecation | null = getDeprecationFor(deprecations, concept.id);
        if (!pendingDep) {
            if (concept.deprecation) {
                pendingDep = getEditableDeprecation(concept);
            } else {
                var deprecationDate: string | null = getDeprecationDate(concept);
                if (deprecationDate) {
                    var rText: string | null = getDeprecationLabel(concept);
                    // Make one up around the existing deprecation model
                    pendingDep = {
                        conceptId: concept.code,
                        reason: rText ? rText : "",
                        deprecationDate: deprecationDate,
                        replacements: [],
                        replacementType: "",
                        new: false,
                        dirty: false
                    }
                }
            }
        }
        if (!pendingDep) {
            return utilityHelper.emptyDeprecation;
        }
        return pendingDep;
    }

    //districtTypeHierarchy: Node
    const getParentList = function (): Array<StyledSelectableListItem> {
        var result: Array<StyledSelectableListItem> = [];

        //TODO ask chris the best way to determine SD or OE
        if (versionedConcept) {
            if (versionedConcept.concept) {
                if (versionedConcept.concept.id.includes("SD_")) {
                    if (specialDistrictNodeSelector) {
                        specialDistrictNodeSelector.outgoing.map(e => {
                            //TODO Note: I'm putting true here to resolve the typescript errors
                            //but I really don't know the implications of this.
                            result.push(addNode(null, e.to, true));
                        });
                    }
                }
                else if (versionedConcept.concept.id.includes("OE_")) {
                    if (otherEntityNodeSelector) {
                        otherEntityNodeSelector.outgoing.map(e => {
                            //TODO Note: I'm putting true here to resolve the typescript errors
                            //but I really don't know the implications of this.
                            result.push(addNode(null, e.to, true));
                        });
                    }
                }
            }
        }
        return result;
    }
    function findNode(current: Node | null, nodeId: string): Node | null  {
        if (!current) {
            return null;
        }
        if (current.concept) {
            if (current.concept.id === nodeId) {
                return current;
            }
        }
        var next : Node | null = null;
        if (current.outgoing && current.outgoing.length > 0) {
            for (var child of current.outgoing) {
                next = findNode(child.to, nodeId);
                if (next) {
                    return next;
                }
            }
        }
        return null;
    }
    function getChildList(unitHierarchy: Node | null): string[] {
        if (!versionedConcept || !versionedConcept.concept) {
            return [];
        }
        var root: Node | null = findNode(unitHierarchy, versionedConcept.concept.id);
        if (!root || !root.outgoing) {
            return [];
        }
        var result: string[] = [];
        root.outgoing.map(e => result.push(e.to.concept.name));
        return result;
;    }
    const addConceptNode = function (parent: ConceptNode | null, n: Node): ConceptNode {
        var nodeLabel = n.name;
        var nvalue = n.id;
        var isEntity = false;
        var nconcept = n.concept;
        if (nconcept) {
            nodeLabel = nconcept.name;
            nvalue = nconcept.id;
            if (!isNaN(parseInt(nconcept.code))) {
                isEntity = true;
            }
        }
        var cnItem: ConceptNode = {
            networkId: "reporting-units",
            concept: n.concept,
            children: [],
            referenceTypes: null
        }
        if (parent) {
            if (!isEntity) {
                parent.children.push(cnItem);
            }
            return cnItem;
        }

        if (n.outgoing) {
            n.outgoing.map(e => addConceptNode(cnItem, e.to));
        }
        return cnItem;
    }

    const getConceptNodeList = function (unitHierarchy: Node | null): Array<ConceptNode> {
        var alist: Array<ConceptNode> = [];
        var selId: string = (versionedConcept && versionedConcept.concept) ? versionedConcept.concept.id : "none";
        if (unitHierarchy && unitHierarchy.outgoing) {
            unitHierarchy.outgoing.map(e => {
                if (e.to.concept.name === "Other Entity" ||
                    e.to.concept.name === "Special District") {
                    var rootItem: ConceptNode = addConceptNode(null, e.to);
                    alist.push(rootItem);
                }
            })

        }
        return alist
    }
    const handleCancelDeprecationClick = () => {
        setEntityDistrictType(null);
        setDeprecationDate("");
        setDocumentation("");
        setMode('inform')
    }

    const handleSaveDeprecationClick = () => {
        if (!versionedConcept) {
            return;
        }
        else {
            //TODO update this to use new actions

            if (versionedConcept.concept) {


                var conceptId = versionedConcept.concept.id;
                var affectedChildren;

                if (versionedConcept.concept.id.includes("SD_")) {
                    if (specialDistrictNodeSelector) {
                        affectedChildren = specialDistrictNodeSelector.outgoing.find(element => element.to.name == conceptId);
                    }
                }
                else if (versionedConcept.concept.id.includes("OE_")) {
                    if (otherEntityNodeSelector) {
                        affectedChildren = otherEntityNodeSelector.outgoing.find(element => element.to.name == conceptId);
                    }
                }

                if (deprecationEditMode == "DEPRECATE_CHILDREN") {
                    /*
                    if (affectedChildren) {
                        if (affectedChildren.to) {
                            for (var i = 0; i < affectedChildren.to.outgoing.length; i++) {

                                var dep: EditDeprecation = {
                                    ...deprecation,
                                    conceptId: affectedChildren.to.outgoing[i].to.name,
                                    deprecationDate: DateTime.fromISO(deprecationDate),
                                    reason: documentation,
                                    dirty: false,
                                    replacementType: "cascadeunits"
                                };
                                setDeprecation(dep);
                                dispatch(addDeprecation(dep));
                                setDeprecationDirty(false);
                            }
                        }
                    }
                    */

                    //deprecate parent
                    var dep: EditDeprecation = {
                        ...deprecation,
                        conceptId: conceptId,
                        deprecationDate: DateTime.fromISO(deprecationDate),
                        reason: documentation,
                        dirty: false,
                        replacementType: "cascadeunits"
                    };
                    setDeprecation(dep);
                    dispatch(addDeprecation(dep));
                    setDeprecationDirty(false);
                }
                else if (deprecationEditMode == "REPARENT_CHILDREN") {

                    var dtDeprecatedId = selectedParentDistrictType;

                    var newParent: DimensionalConceptRef = {
                        conceptId: dtDeprecatedId,
                        dimensions: [],
                        conceptName: "",
                        taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
                        afrYear: versionedConcept.year //ask chris, should this be empty or is taxonomy year okay?
                    };

                    var dep: EditDeprecation = {
                        conceptId: conceptId,
                        deprecationDate: DateTime.fromISO(deprecationDate),
                        reason: documentation,
                        replacementType: "reparentunits", // one of simple, aggregateToPart, partToAggregate
                        replacements: [newParent],
                        new: false,
                        dirty: false,
                    }

                    setDeprecation(dep);
                    dispatch(addDeprecation(dep));
                    setDeprecationDirty(false);
                }
            }
        }
    }

    const createParentRelationship = function (parent: Concept): RelationshipInput {

        var rel: RelationshipInput = {
            fromId: parent.id,
            networkId: "reporting-units",
            role: "domain-member",
            toId: "",
            remove: false
        };
        return rel;
    }

    const handleCodeValidation = function (value: string): boolean {
        return value.length > 0;
    }

    const deprecationFormIsValid = (): boolean => {
        var result = false;

        if (mode == 'deprecate') {
            if (DateTime.fromISO(deprecationDate).isValid
                && utilityHelper.isTextPresent(documentation)) {
                if (deprecationEditMode == "DEPRECATE_CHILDREN"
                    || (deprecationEditMode == "REPARENT_CHILDREN" && selectedParentDistrictType != "")) {
                    result = true;
                }
            }
        }

        return result;
    }

    const handleCloseConceptModal = function (event: any) {

        if (event && event.hasOwnProperty("newconcept")) {
            setDeprecation(emptyDeprecation);
            var newVersionedConcept: DtsConcept = {
                parent: versionedConcept,
                concept: event.newconcept,
                year: versionInfo ? versionInfo.year : 2022,
                entityScope: null,
                dimensionalBreakdowns: null,
                dimensionalScope: null,
                dependencies: null,
                new: true
            }

            dispatch(setSelectedTaxonomyConcept(newVersionedConcept));
            dispatch(clearSelectedReference());

            setNewDistrictTypeName(event.newconcept.name);
            setNewDistrictTypeCode(event.newconcept.code);
        }
        else {
            setEntityDistrictType(null);
            setMode('inform')
            setNewDistrictTypeName('');
            setNewDistrictTypeCode('');
        }
        setModalCreateConceptIsOpen(false);

    }

    const handleNewDistrictClick = () => {

        setMode("create");
        setModalCreateConceptIsOpen(true);

    }

    const leftTopMarginStyle = {
        marginTop: "1em",
        marginLeft: "1em"
    }

    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }

    const leftMarginStyle = {
        marginLeft: '1em'
    }

    //local hooks

    const [selectedDistrictType, setSelectedDistrictType] = React.useState('');
    const [selectedParentDistrictType, setSelectedParentDistrictType] = React.useState('');

    const [deprecationDate, setDeprecationDate] = React.useState<string | undefined>(undefined);
    const [documentation, setDocumentation] = React.useState(concept ? concept.documentation : "");
    const [deprecationDirty, setDeprecationDirty] = React.useState(false);

    const [newDistrictTypeName, setNewDistrictTypeName] = React.useState<string>('');
    const [newDistrictTypeCode, setNewDistrictTypeCode] = React.useState<string>('');
    const [modalCreateConceptIsOpen, setModalCreateConceptIsOpen] = React.useState(false);
    const emptyDeprecation: any = { conceptId: "", deprecationDate: "", reason: "", replacements: [], replacementType: "", new: false, dirty: false };
    const [entityDistrictType, setEntityDistrictType] = React.useState<"Special District" | "Other Entity" | null>(null);
    const [deprecationEditMode, setDeprecationEditMode] = React.useState<"OFF" | "DEPRECATE_CHILDREN" | "REPARENT_CHILDREN">("OFF");


    async function dropDownHandleChange(currentNode: any, selectedNodes: any) {
        setMode("inform");
        setDeprecationDate("");
        setDocumentation("");
        setDeprecationDirty(false);
        if (selectedNodes.length === 0) {
            setSelectedDistrictType("");
            setNewDistrictTypeName("");
            setNewDistrictTypeCode("");
            return;
        }

        var conceptId = selectedNodes[0].value;
        setSelectedDistrictType(conceptId);

        var vconcept: any = null;
        if (conceptChanges && versionInfo) {
            var cchg: ConceptInput | undefined = conceptChanges.find((cc:any) => cc.id === conceptId);
            if (cchg == null) {
                vconcept = null;
            } else {
                vconcept = {
                    concept: {
                        id: cchg.id,
                        name: getChangeConceptLabel(cchg, "label"),
                        code: getChangeConceptLabel(cchg, "code"),
                        documentation: "",
                        datatype: cchg.datatype,
                        deprecation: null,
                        domainDatatype: "",
                        labels: [],
                        references: [],
                        isAbstract: cchg.isAbstract,
                        isCredit: cchg.isCredit,
                        periodtype: cchg.periodtype,
                        isDeprecated: false
                    },
                    year: versionInfo.year,
                    new: true
                }
            }
        }
        var ref: ConceptRef = {
            conceptId: conceptId,
            taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
            afrYear: 2022
        }
        setDeprecation(emptyDeprecation);
        if (vconcept) {
            await dispatch(setSelectedTaxonomyConcept(vconcept));
        } else {
            await dispatch(loadTaxonomyConcept(ref));
        }
        await dispatch(clearSelectedReference());
    }

    async function dropDownHandleChangeTwo(currentNode: any, selectedNodes: any) {
        if (selectedNodes.length === 0) {
            setSelectedParentDistrictType("");
        }
        else {
            var conceptId = selectedNodes[0].value;
            setSelectedParentDistrictType(conceptId);
        }
    }

    const onRadioChangeValue = (event: { target: { value: string; }; }) => {

        switch (event.target.value) {
            case "DEPRECATE_CHILDREN":
                setDeprecationEditMode('DEPRECATE_CHILDREN');
                setSelectedParentDistrictType("");
                break;
            case "REPARENT_CHILDREN":

                setDeprecationEditMode('REPARENT_CHILDREN');
                break;
            default:
                setDeprecationEditMode('OFF');
                setSelectedParentDistrictType("");
                break;
        }
    }

    React.useEffect(() => {
        if (versionedConcept && versionedConcept.concept) {
            setNewDistrictTypeName(versionedConcept.concept.name);
            setNewDistrictTypeCode(versionedConcept.concept.code);
            setChildList(getChildList(unitHierarchySelector));
        }
    }, [versionedConcept]);



    const districtTypeSelector = React.useMemo(() => {
        return (
            <DropdownTreeSelect
                data={getUnitsList(unitHierarchySelector, conceptChanges, relationshipChanges, versionedConcept)}
                mode="radioSelect" keepTreeOnSearch={true}
                onChange={dropDownHandleChange}
                texts={{ placeholder: "Choose District Type..." }} />
        )
    }, [conceptChanges, unitHierarchySelector]);


    const newParentSelector = React.useMemo(() => {
        return (
            <DropdownTreeSelect
                data={getParentList()}
                mode="radioSelect" keepTreeOnSearch={true}
                onChange={dropDownHandleChangeTwo}
                texts={{ placeholder: "Choose District Type..." }} />
        )
    }, [conceptChanges, unitHierarchySelector, versionedConcept]);

    return (
        <React.Fragment>
            <Row>
                <Col>
                    <br />
                    <br />
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <h5>
                        <React.Fragment>
                            {districtTypeSelector}
                        </React.Fragment>
                    </h5>
                </Col>
                <Col style={{ textAlign: "right" }}>
                    <Button type="button"
                        style={leftMarginStyle}
                        className="taxedit"
                        onClick={handleNewDistrictClick}
                        disabled={(selectedDistrictType != "SpecialDistrict" && selectedDistrictType != "OtherEntity")}>Create District Type</Button>
                    <Button type="button"
                        style={leftMarginStyle}
                        className="taxedit"
                        disabled={selectedDistrictType == "SpecialDistrict" || selectedDistrictType == "OtherEntity" || selectedDistrictType == ""}
                        onClick={(e) => { setMode("deprecate") }}>Deprecate District Type</Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Collapse isOpen={true}>
                        <Col>
                            <FormGroup>
                                <Row>
                                    <Col className="text-md-right" >
                                        <Label><b>Name:</b></Label>
                                    </Col>
                                    <Col lg="10">
                                        <Input type="text" value={newDistrictTypeName}
                                            onChange={(e) => setNewDistrictTypeName(e.currentTarget.value)}
                                            disabled={true}
                                            valid={utilityHelper.isTextPresent(newDistrictTypeName) && (mode == "create")}
                                            invalid={!utilityHelper.isTextPresent(newDistrictTypeName) && (mode == "create")}
                                        />
                                        <FormFeedback>District type name is required.</FormFeedback>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col className="text-md-right" >
                                        <Label><b>Code:</b></Label>
                                    </Col>
                                    <Col lg="10">
                                        <Input type="text" value={newDistrictTypeCode}
                                            onChange={(e) => setNewDistrictTypeCode(e.currentTarget.value)}
                                            disabled={true}
                                        />
                                        <FormFeedback>District type name is required.</FormFeedback>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>

                    </Collapse>
                    <Collapse isOpen={mode == "deprecate" && !modalCreateConceptIsOpen}>
                        <Row>
                            <Col md="4" >
                                <Row>
                                    <Col>
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <ul>
                                                        <li>
                                                            <Input
                                                                type="radio"
                                                                value={"DEPRECATE_CHILDREN"}
                                                                name="deprecation-mode-edit"
                                                                onChange={onRadioChangeValue}

                                                                checked={deprecationEditMode == "DEPRECATE_CHILDREN"}
                                                            />Deprecate Children</li>
                                                        <li>
                                                            <Input
                                                                type="radio"
                                                                value={"REPARENT_CHILDREN"}
                                                                name="deprecation-mode-edit"
                                                                onChange={onRadioChangeValue}

                                                                checked={deprecationEditMode == "REPARENT_CHILDREN"}
                                                            />Reparent Children</li>
                                                    </ul>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup>
                                            <Collapse isOpen={mode == "deprecate" && !modalCreateConceptIsOpen
                                                && deprecationEditMode == "REPARENT_CHILDREN"}>
                                                <Row>
                                                    <Col>
                                                        {newParentSelector}
                                                    </Col>
                                                </Row>
                                            </Collapse>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label>Entities belonging to <b>{newDistrictTypeName}</b></Label>
                                    <ListGroup>
                                        {childList.map((option, idx) => (
                                            <ListGroupItem>{option}</ListGroupItem>
                                        ))}
                                    </ListGroup>
                                </Row>
                            </Col>
                            <Col md="8">
                                <FormGroup>
                                    <Row>
                                        <Col className="text-md-right" >
                                            <Label><b>Date:</b></Label>
                                        </Col>
                                        <Col lg="10">

                                            <Input type="date" value={deprecationDate}
                                                onChange={(e) => { setDeprecationDate(e.currentTarget.value); setDeprecationDirty(true); }}
                                                valid={DateTime.fromISO(deprecationDate).isValid && (mode == "deprecate")}
                                                invalid={!DateTime.fromISO(deprecationDate).isValid && (mode == "deprecate")} />
                                            <FormFeedback>Enter a valid date.</FormFeedback>
                                        </Col>
                                    </Row>
                                </FormGroup>
                                <FormGroup>
                                    <Row>
                                        <Col className="text-md-right" >
                                            <Label><b>Comments:</b></Label>
                                        </Col>
                                        <Col md="10">
                                            <Input type="textarea" value={documentation} rows="10"
                                                onChange={(e) => setDocumentation(e.currentTarget.value)}
                                                valid={utilityHelper.isTextPresent(documentation) && (mode == "deprecate")}
                                                invalid={!utilityHelper.isTextPresent(documentation) && (mode == "deprecate")}
                                            />
                                            <FormFeedback>Comment required.</FormFeedback>
                                        </Col>
                                    </Row>
                                </FormGroup>
                                <FormGroup>
                                    <Row>
                                            <div style={flexRightChild} >
                                                <Button
                                                    type="button"
                                                    style={leftMarginStyle}
                                                    className="taxedit"
                                                    disabled={!deprecationFormIsValid()}
                                                    onClick={handleCancelDeprecationClick}>Undo</Button>
                                                <Button
                                                    type="button"
                                                    style={leftMarginStyle}
                                                    className="taxedit"
                                                    disabled={!deprecationFormIsValid()}
                                                    onClick={handleSaveDeprecationClick}>Save Deprecation</Button>
                                            </div>

                                    </Row>
                                </FormGroup>

                            </Col>
                        </Row>
                    </Collapse>
                </Col>
            </Row>

            {versionedConcept && versionedConcept.concept && (<CreateConceptModal
                isOpen={modalCreateConceptIsOpen}
                parentName={selectedDistrictType}
                parentCode={versionedConcept.concept.name}
                parentRelationship={createParentRelationship(versionedConcept.concept)}
                conceptType="district type"
                codeValidator={handleCodeValidation}
                onCloseModal={handleCloseConceptModal}
                conceptChanges={conceptChanges}
                accountTree={getConceptNodeList(unitHierarchySelector)}
                isMemberConcept={true} />)}
        </React.Fragment>
    );

}

export default DistrictTypeManager;